// Voice mode's view state (BUILD 2 C2-8, WP2-8 step 1): a zustand vanilla
// store the controller writes and the bar, the pending bubble and the entry
// read. The state machine itself is the controller (./controller.ts):
//
//   off ─enter(tap)─► arming (claim 'voice', resume the AudioContext, wake lock, mint, open)
//   arming ─ready─► ready
//   ready ─tap─► hearing (live text in the composer row)
//   hearing ─tap ✓ | HF endpoint ≥ 1 word─► committing ─committed─► pending
//   pending ─window ∧ G1–G8─► sending ─first text─► answering ─first audio─► speaking ─drained─► ready
//   pending ─Esc/×─► ready (no request)
//   pending ─Edit─► editing (text → composer; voice paused)
//   pending ─speech resumes─► hearing (the same turn, merged)
//   speaking ─tap─► stop ≤ 150 ms ─► hearing | ready
//   speaking ─local onset above playback─► ducked ─sustained speech─► hearing
//   any ─hidden | meeting claim | exit | sign-out─► off or paused (a pending turn becomes a draft)
//
// The live text and the pending bubble's text are the provider's partials and
// finals, for display only; what is sent is the committed utterance.
import { createStore, type StoreApi } from 'zustand/vanilla';
import { upsertSegment, type CaptionSegment } from '@/speech/interactive/store';
import type { VoicePhase, VoiceStyle } from './contracts';
import type { VoiceIssue } from './issues';

export { upsertSegment };
export type { CaptionSegment };

/** Why a pending turn waits: its countdown, or a tap it needs. */
export type PendingReason =
  | 'countdown'
  | 'always_ask'
  | 'degraded'
  | 'focus'
  | 'endpoint'
  | 'paused';

export type PendingView = {
  id: number;
  /** The provider's finals for the turn: display only. */
  segments: CaptionSegment[];
  /** When the countdown sends it (the controller's clock), or null while it waits for a tap. */
  deadline: number | null;
  undoMs: number;
  reason: PendingReason;
};

export type VoiceState = {
  phase: VoicePhase;
  style: VoiceStyle;
  /** The open turn's live text: greyed partials, then finals. */
  segments: CaptionSegment[];
  pending: PendingView | null;
  /** Smoothed 0..1 microphone level for the waveform. */
  level: number;
  /** The microphone is claimed by voice mode: the indicator shows. */
  micClaimed: boolean;
  /** The provider switched to a backup engine or to utterance mode. */
  degraded: boolean;
  reconnecting: boolean;
  /** Why voice mode paused or ended, or what needs attention. */
  issue: VoiceIssue | null;
  /** Increments once per pending turn, for its single polite announcement. */
  announced: number;
};

export const INITIAL_VOICE_STATE: VoiceState = Object.freeze({
  phase: 'off',
  style: 'handsfree',
  segments: [],
  pending: null,
  level: 0,
  micClaimed: false,
  degraded: false,
  reconnecting: false,
  issue: null,
  announced: 0,
}) as VoiceState;

export type VoiceStore = StoreApi<VoiceState>;

export const createVoiceStore = (initial: Partial<VoiceState> = {}): VoiceStore =>
  createStore<VoiceState>(() => ({ ...INITIAL_VOICE_STATE, ...initial }));

/** Phases in which the voice bar holds the composer. */
export function barActive(phase: VoicePhase): boolean {
  return phase !== 'off' && phase !== 'paused' && phase !== 'editing';
}

/** Phases in which a reply is on its way or playing. */
export function replying(phase: VoicePhase): boolean {
  return phase === 'sending' || phase === 'answering' || phase === 'speaking' || phase === 'ducked';
}
